import { Calendar, CalendarPreference } from '@demind-inc/core';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useRecoilState } from 'recoil';

import {
  useAuthContext,
  useCalendarsMeta,
  usePreferences,
  useUpdateCalendarInfo,
  visibleCalendarIdsAtom,
} from '../../data-access';
import {
  isAppleCalendarDesktopSupported,
  isReadOnlyAppleBirthdayCalendarDesktop,
} from '../../helpers/appleCalendarDesktop.helpers';
import { hasReadOnlyCalendarScope } from '../helpers/calendarAccess.helpers';
import { applyWeekStartDay, getSavedWeekStartDay } from '../helpers/weekStart.helpers';

interface ICalendarContext {
  calendarsMeta: Calendar[];
  visibleCalendarIds: string[];
  mainCalendar?: Calendar;
  calendarAccountsToCreateEvents: Calendar[];
  calendarPreferences: CalendarPreference;
  isFetchingCalendars: boolean;
  findCalendarItem: (targetCalendarId: string) => Calendar | undefined;
  checkCalendarHassAccessToModifyEvent: (targetCalendarId: string) => boolean;
  handleSelectCalendar: (targetCalendar: Calendar, checked: boolean) => void;
}

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const CalendarContext = createContext({} as ICalendarContext);
export const useCalendarContext = () => useContext(CalendarContext);

export const CalendarProvider = ({ children }: { children: ReactNode }) => {
  const [visibleCalendarIds, setVisibleCalendarIds] = useRecoilState(visibleCalendarIdsAtom);

  const { user } = useAuthContext();
  const { calendarsMeta, isLoading: isFetchingCalendars } = useCalendarsMeta({
    userId: user.userId,
  });

  const { updateCalendarInfo } = useUpdateCalendarInfo();
  const { calendarPreferences } = usePreferences({ preferenceId: user.preferenceId! });

  const normalizedCalendarPreferences = useMemo((): CalendarPreference => {
    const toBool = (value: unknown): boolean =>
      value === true || value === 'true' || value === 1 || value === '1';

    const hideAutoScheduleRaw = (calendarPreferences as CalendarPreference | undefined)
      ?.hideAutoSchedule as unknown;
    const hideAutoScheduleObj =
      hideAutoScheduleRaw && typeof hideAutoScheduleRaw === 'object'
        ? (hideAutoScheduleRaw as Record<string, unknown>)
        : {};

    return {
      ...(calendarPreferences as CalendarPreference),
      hideAutoSchedule: {
        completedTask: toBool(hideAutoScheduleObj.completedTask),
        routine: toBool(hideAutoScheduleObj.routine),
        exercise: toBool(hideAutoScheduleObj.exercise),
      },
    } as CalendarPreference;
  }, [calendarPreferences]);

  // Applied during render (not in an effect) so children already see the right first weekday.
  const weekStartDay = getSavedWeekStartDay(calendarPreferences);
  useMemo(() => applyWeekStartDay(weekStartDay), [weekStartDay]);

  const mainCalendar = useMemo(() => {
    // First check if there's a default calendar specified in preferences
    if (calendarPreferences.defaultCalendarId !== undefined) {
      return calendarsMeta.find(
        (info) => info.calendarId === calendarPreferences.defaultCalendarId
      );
    }

    // Try to find a Google or Outlook calendar first
    const googleOrOutlookCalendar = calendarsMeta.find(
      (info) =>
        info.calendarType !== 'lifestack' &&
        (info.default === true ||
          (info.default === undefined && info?.internalGCalendarId === user.email))
    );

    if (googleOrOutlookCalendar) {
      return googleOrOutlookCalendar;
    }

    // If no Google/Outlook calendar is found, fall back to Lifestack calendar
    return calendarsMeta.find(
      (info) =>
        info.calendarType === 'lifestack' &&
        (info.default === true ||
          (info.default === undefined && info?.internalGCalendarId === user.email))
    );
  }, [calendarsMeta, user.email, calendarPreferences]);

  const calendarAccountsToCreateEvents = useMemo(
    () =>
      calendarsMeta.filter((calendar) => {
        const isReadOnlyScope = hasReadOnlyCalendarScope(calendar);
        const isReadOnlyAppleBirthday = isReadOnlyAppleBirthdayCalendarDesktop(calendar);
        const isAppleCalendarOnUnsupportedPlatform =
          calendar.calendarType === 'apple' && !isAppleCalendarDesktopSupported();

        return (
          !isReadOnlyScope && !isReadOnlyAppleBirthday && !isAppleCalendarOnUnsupportedPlatform
        );
      }),
    [calendarsMeta]
  );

  // Update the visible calendar ids when the calendar meta is fetched
  useEffect(() => {
    if (!calendarsMeta.length) {
      return;
    }

    setVisibleCalendarIds(
      calendarsMeta
        .filter((info) => !!info && info.visible)
        .map((info) => info?.calendarId ?? [])
        .flat()
    );
  }, [JSON.stringify(calendarsMeta)]);

  const findCalendarItem = useCallback(
    (targetCalendarId: string) => {
      return calendarsMeta.find(({ calendarId }) => calendarId === targetCalendarId);
    },
    [calendarsMeta]
  );

  const checkCalendarHassAccessToModifyEvent = useCallback(
    (targetCalendarId: string) => {
      const targetCalendar = findCalendarItem(targetCalendarId);
      if (!targetCalendar) {
        return false;
      }

      const isReadOnlyScope = hasReadOnlyCalendarScope(targetCalendar);
      const isReadOnlyAppleBirthday = isReadOnlyAppleBirthdayCalendarDesktop(targetCalendar);
      const isAppleCalendarOnUnsupportedPlatform =
        targetCalendar.calendarType === 'apple' && !isAppleCalendarDesktopSupported();

      return !isReadOnlyScope && !isReadOnlyAppleBirthday && !isAppleCalendarOnUnsupportedPlatform;
    },
    [findCalendarItem]
  );

  const handleSelectCalendar = useCallback((targetCalendar: Calendar, checked: boolean) => {
    if (checked) {
      setVisibleCalendarIds((prev) => [...prev, targetCalendar.calendarId]);
    } else {
      setVisibleCalendarIds((prev) => prev.filter((id) => id !== targetCalendar.calendarId));
    }
    updateCalendarInfo({
      calendarId: targetCalendar.calendarId,
      newCalendarInfo: { visible: checked },
    });
  }, []);

  return (
    <CalendarContext.Provider
      value={{
        mainCalendar,
        visibleCalendarIds,
        calendarsMeta,
        calendarPreferences: normalizedCalendarPreferences,
        calendarAccountsToCreateEvents,
        isFetchingCalendars,
        findCalendarItem,
        handleSelectCalendar,
        checkCalendarHassAccessToModifyEvent,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};
